import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MenuItem, TextField, makeStyles } from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import { colorSet, clsx } from '@components/Provider';
import {
  CustomizedTooltip,
  CustomizedTypography
} from '@components/Customized';
import {
  CapaQuotationServiceType,
  CapaRfqPurchaseOrderTransfer,
  CapaRfqPurchaseOrderTransferTermsType,
  CreatePurchaseOrderInput
} from '@generated/graphql';
import CreateOrderCommonInputBox from './CreateOrderCommonInputBox';
import { CreateOrderFormSection } from './styles';
import { formatDate } from '@utils/formatter';

export enum TransferTermType {
  NO_TRANSFER = 'NO_TRANSFER'
}

interface CreateOrderMoldTransferProps {
  serviceType: CapaQuotationServiceType;
  baseData?: CapaRfqPurchaseOrderTransfer;
}

const CreateOrderMoldTransfer: React.FC<CreateOrderMoldTransferProps> = ({
  serviceType,
  baseData
}) => {
  const [selectedCondition, setSelectedCondition] = useState<
    CapaRfqPurchaseOrderTransferTermsType | undefined
  >(baseData?.transferTerms ?? undefined);
  const {
    conditionSelect,
    placeholder,
    inputEndAdorment,
    keeping,
    transferDate,
    tooltipContent
  } = useCreateOrderMoldTransferStyles();
  const { register, errors, watch, clearErrors, control } = useFormContext<
    CreatePurchaseOrderInput
  >();
  const shipDate = watch('shipDate');

  useEffect(() => {
    if (selectedCondition) {
      clearErrors('transferTerms');
    }
  }, [selectedCondition, clearErrors]);

  return (
    <CreateOrderFormSection>
      <CustomizedTypography
        variant="h4"
        fontWeight="500"
        className={transferDate}
        gutterBottoms={8}
      >
        {serviceType === CapaQuotationServiceType.Inj
          ? '금형'
          : serviceType === CapaQuotationServiceType.Casting
          ? '주형'
          : ''}{' '}
        이관 정보
      </CustomizedTypography>
      <CreateOrderCommonInputBox
        label="이관 조건"
        style={{ marginBottom: 8 }}
        labelIcon={
          <CustomizedTooltip
            title={
              <CustomizedTypography
                variant="body2"
                fontWeight="500"
                className={tooltipContent}
              >
                금형 제조결과물을 납품 시 이관할지, 일정 기간 보관 후 이관할 지
                조건을 선택해주세요.
              </CustomizedTypography>
            }
            zIndex={1500}
          >
            <InfoOutlined />
          </CustomizedTooltip>
        }
        input={
          <Controller
            name="transferTerms"
            rules={{ required: '이관 정보를 입력해주세요' }}
            control={control}
            defaultValue={baseData?.transferTerms ?? undefined}
            render={(contorlProps) => (
              <TextField
                {...contorlProps}
                className={clsx([
                  conditionSelect,
                  !selectedCondition ? placeholder : ''
                ])}
                select
                SelectProps={{
                  displayEmpty: true
                }}
                error={!!errors.transferTerms}
                helperText={
                  errors?.transferTerms?.message ||
                  (selectedCondition ===
                    CapaRfqPurchaseOrderTransferTermsType.TransferAtShip && (
                    <CustomizedTypography
                      variant="subtitle2"
                      className={transferDate}
                      align={'left'}
                    >
                      {formatDate(shipDate as string, 'YYYY년 MM월 DD일')}에
                      이관합니다
                    </CustomizedTypography>
                  ))
                }
                variant="outlined"
                fullWidth
                onChange={(e) => {
                  contorlProps.onChange(e);
                  setSelectedCondition(
                    e.target.value as CapaRfqPurchaseOrderTransferTermsType
                  );
                }}
              >
                <MenuItem disabled>이관 조건 선택</MenuItem>
                <MenuItem
                  value={CapaRfqPurchaseOrderTransferTermsType.TransferAtShip}
                >
                  납품 시 이관
                </MenuItem>
                <MenuItem
                  value={CapaRfqPurchaseOrderTransferTermsType.StorageAfterShip}
                >
                  납품 후 일정 기간 보관
                </MenuItem>
                <MenuItem value={TransferTermType.NO_TRANSFER}>
                  해당사항 없음
                </MenuItem>
              </TextField>
            )}
          />
        }
      />
      {selectedCondition ===
        CapaRfqPurchaseOrderTransferTermsType.StorageAfterShip && (
        <CreateOrderCommonInputBox
          label="보관 기간"
          input={
            <TextField
              inputProps={{
                className: keeping,
                placeholder: '0',
                ref: register({
                  required: {
                    value:
                      selectedCondition ===
                      CapaRfqPurchaseOrderTransferTermsType.StorageAfterShip,
                    message: '보관 기간을 입력해주세요.'
                  },
                  validate: {
                    biggerThanZero: (value) =>
                      Number(value) > 0 ? true : '1개월 이상 입력해주세요.'
                  }
                }),
                name: 'transferStorage'
              }}
              type="number"
              variant="outlined"
              fullWidth
              error={Boolean(errors?.transferStorage)}
              helperText={errors?.transferStorage?.message}
              InputProps={{
                endAdornment: (
                  <CustomizedTypography
                    variant="body2"
                    className={inputEndAdorment}
                    color={'textSecondary'}
                  >
                    개월
                  </CustomizedTypography>
                )
              }}
            />
          }
        />
      )}
    </CreateOrderFormSection>
  );
};

export default CreateOrderMoldTransfer;

const useCreateOrderMoldTransferStyles = makeStyles((theme) => ({
  conditionSelect: {
    '& .MuiSelect-selectMenu': {
      paddingTop: 7,
      paddingBottom: 7
    },
    '& .MuiFormHelperText-root': {
      left: 0,
      '&.Mui-error': {
        left: 0
      }
    }
  },
  placeholder: {
    '& .MuiSelect-selectMenu': {
      color: colorSet.gray500,
      opacity: 1
    }
  },
  inputEndAdorment: {
    whiteSpace: 'nowrap'
  },
  keeping: {
    textAlign: 'right',
    marginRight: 4
  },
  transferDate: {
    marginTop: '-2px'
  },
  tooltipContent: {
    color: colorSet.gray000
  }
}));
